import React, { useState } from "react";
import axios from "axios";
import baseUrl from "../../../api/universalApi";
import toast from "react-hot-toast";
import { motion } from "framer-motion";

const UpdateInvoice = ({ campaignDetails, fetchAllCampaigns }) => {
  const initialData = campaignDetails?.invoice || {};

  const [formData, setFormData] = useState({
    campaign_id: initialData.campaign_id || "",
    rep_email: initialData.rep_email || "",
    invoice_file: initialData?.invoice_file || null,
    invoice_link: initialData?.invoice_link || "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "file" ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.invoice_file && !formData.invoice_link) {
      alert("Please upload an invoice file or provide a link.");
      return;
    }

    const filedata = new FormData();
    if (formData.invoice_file) {
      const fileName = Date.now() + formData.invoice_file.name;
      filedata.append("name", fileName);
      filedata.append("file", formData.invoice_file);
    }
    filedata.append("rep_email", formData.rep_email);
    filedata.append("campaign_id", formData.campaign_id);
    filedata.append("invoice_link", formData.invoice_link);

    try {
      const response = await axios.post(
        `${baseUrl}/api/invoice/updateInvoice`,
        filedata,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.status === 200) {
        toast.success("Invoice updated successfully");
        fetchAllCampaigns();
      } else toast.error("Failed to update invoice");
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Error occurred while updating");
    }
  };

  const handleOpen = async (campaign_id) => {
    try {
      const response = await fetch(`${baseUrl}/api/invoice/openedInvoice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ campaign_id })
      });
      if (!response.ok) throw new Error("Failed to fetch contract details");
      await response.json();
      fetchAllCampaigns();
      toast.success("Invoice Opened");
    } catch (error) {
      toast.error("something went wrong");
    }
  };

  const handleApprove = async (campaign_id, status) => {
    try {
      const response = await fetch(`${baseUrl}/api/invoice/approveInvoice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ campaign_id, status })
      });
      if (!response.ok) throw new Error("Failed to approve invoice");
      await response.json();
      fetchAllCampaigns();
      toast.success("Invoice Approved");
    } catch (error) {
      toast.error("something went wrong");
    }
  };

  const handleSendToFinance = async (invoice) => {
    try {
      const response = await fetch(`${baseUrl}/api/invoice/sendInvoiceToFinance`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaign_id: invoice.campaign_id,
          contract_id: invoice.contract._id,
          amount: invoice.amount,
          company: invoice.contract.company,
        })
      });
      if (!response.ok) throw new Error("Failed to send invoice to finance");
      await response.json();
      fetchAllCampaigns();
      toast.success("Invoice sent to finance");
    } catch (error) {
      toast.error("something went wrong");
    }
  };

  const handleProcessed = async (invoice) => {
    try {
      const response = await fetch(`${baseUrl}/api/invoice/markAsProcessed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ campaign_id: invoice.campaign_id })
      });
      if (!response.ok) throw new Error("Failed to mark as processed");
      await response.json();
      fetchAllCampaigns();
      toast.success("Invoice Processed");
    } catch (error) {
      toast.error("something went wrong");
    }
  };

  const steps = [
    { key: "openedAt", label: "Opened" },
    { key: "approvedAt", label: "Approved" },
    { key: "sent_to_finance", label: "Finance" },
    { key: "processed", label: "Processed" },
  ];

  const getStepStatus = (invoice) => [
    !!invoice?.openedAt,
    !!invoice?.approvedAt,
    !!invoice?.sent_to_finance,
    !!invoice?.processed,
  ];

  return (
    <>
      {campaignDetails?.invoice?.rep_email ? (
        <div>
          <h2 className="text-lg font-bold mb-4">Update Invoice</h2>
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            encType="multipart/form-data"
          >
            <div>
              <label className="block text-sm font-medium">Rep Email</label>
              <input
                type="email"
                name="rep_email"
                value={formData.rep_email}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Upload Invoice (PDF)</label>
              <input
                type="file"
                name="invoice_file"
                accept="application/pdf"
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Or Invoice Link</label>
              <input
                type="url"
                name="invoice_link"
                value={formData.invoice_link}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
                placeholder="https://drive.google.com/..."
              />
            </div>

            {(formData.invoice_file || formData.invoice_link) && (
              <div>
                <p className="mb-0">View Invoice</p>
                <a
                  href={formData.invoice_file || formData.invoice_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Invoice
                </a>
              </div>
            )}

            <div className="col-span-1 md:col-span-2 flex flex-col items-center">
              <div className="flex items-center mb-6">
                {steps.map((step, idx) => {
                  const statusArr = getStepStatus(campaignDetails.invoice);
                  const isActive = statusArr[idx];
                  const isNext = !isActive && (idx === 0 || statusArr[idx - 1]);
                  return (
                    <React.Fragment key={step.key}>
                      <motion.button
                        type="button"
                        className={`rounded-full px-3 py-1 flex items-center justify-center font-semibold text-white focus:outline-none transition-colors duration-300
                          ${isActive ? "bg-green-600" : isNext ? "bg-gray-400" : "bg-gray-200 text-gray-500"}
                        `}
                        whileTap={{ scale: 0.9 }}
                        whileHover={isNext ? { scale: 1.1 } : {}}
                        disabled={!isNext && !isActive}
                        onClick={() => {
                          if (isNext) {
                            if (step.key === "openedAt") handleOpen(campaignDetails?._id);
                            else if (step.key === "approvedAt") handleApprove(campaignDetails?._id, "approved");
                            else if (step.key === "sent_to_finance") handleSendToFinance(campaignDetails?.invoice);
                            else if (step.key === "processed") handleProcessed(campaignDetails?.invoice);
                          }
                        }}
                      >
                        {step.label}
                      </motion.button>
                      {idx < steps.length - 1 && (
                        <motion.div
                          className={`h-1 w-10 mx-2 rounded-full ${statusArr[idx] ? "bg-green-600" : "bg-gray-300"}`}
                          layout
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <button
                type="submit"
                className="bg-blue-600 text-white rounded-full px-3 py-1 hover:bg-blue-700"
              >
                Update Invoice
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="w-full">
          <h1 className="text-lg font-bold mb-4">No Invoice Found</h1>
          <p className="text-sm text-gray-600">Please create an Invoice first.</p>
        </div>
      )}
    </>
  );
};

export default UpdateInvoice;